import React, { useState, useEffect } from 'react';
import { salesService } from '../../services';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  CircularProgress,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { CONTROL_STYLE } from '../../constants/invoiceConstants';

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @param {string} currencyIcon - Optional image source for currency icon
 * @returns {string|React.ReactNode} - Formatted currency string or React node
 */
const formatCurrency = (amount, currencyIcon) => {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
  
  if (currencyIcon) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <img src={currencyIcon} alt="currency" style={{ width: '15px', height: '15px', objectFit: 'contain' }} />
        <span>{formatted}</span>
      </Box>
    );
  }
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'SAR',
    minimumFractionDigits: 2
  }).format(amount);
};

/**
 * Format date for display
 * @param {string} dateString - Date string to format
 * @returns {string} - Formatted date string
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

/**
 * Get color for payment status
 * @param {string} status - Payment status
 * @returns {string} - Color name for the status
 */
const getStatusColor = (status) => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'partial':
      return 'warning';
    case 'pending':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Dialog for selecting an invoice to create a credit note from
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to call when dialog is closed
 * @param {Function} props.onSelectInvoice - Function to call when an invoice is selected
 * @param {string} [props.dialogTitle] - Custom title for the dialog
 * @param {string} [props.currencyIcon] - Optional currency icon
 */
export default function SelectInvoiceDialog({ open, onClose, onSelectInvoice, dialogTitle = 'Select Invoice for Credit Note', currencyIcon }) {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalRows, setTotalRows] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  // Default invoices state to show when no search is performed
  const [defaultInvoices, setDefaultInvoices] = useState([]);

  // Load default invoices when dialog opens and reset state when dialog closes
  useEffect(() => {
    if (open) {
      fetchDefaultInvoices();
    } else {
      // Clear search and results when dialog is closed
      setSearchQuery('');
      setInvoices([]);
      setPage(0);
    }
  }, [open]);
  
  // Only update pagination when page or rows per page changes
  useEffect(() => {
    // Only fetch if we have already performed a search (invoices has data)
    if (invoices.length > 0 && open) {
      fetchInvoices();
    }
  }, [page, rowsPerPage]);

  // Fetch default 5 invoices when dialog opens
  const fetchDefaultInvoices = async () => {
    setLoading(true);
    try {
      const data = await salesService.getSalesInvoices({}, 1, 5);
      setDefaultInvoices(data.data);
      setTotalRows(data.pagination.total);
    } catch (error) {
      console.error('Error fetching default invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch invoices based on search query using the dedicated search endpoint
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      console.log('Searching for invoices with query:', searchQuery.trim());
      const data = await salesService.searchSalesInvoices(searchQuery.trim(), page + 1, rowsPerPage);
      console.log('API response data:', data);
      if (data.success) {
        console.log('Search successful, found', data.data.length, 'invoices');
        // Use the data directly from the API without any client-side filtering
        setInvoices(data.data);
        setTotalRows(data.pagination.total);
      } else {
        console.error('API returned success: false', data);
        setInvoices([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    console.log('Search button clicked, search query:', searchQuery);
    if (searchQuery.trim()) {
      console.log('Search query is valid, fetching invoices...');
      setPage(0);
      fetchInvoices();
    } else {
      console.log('Search query is empty, no search performed');
    }
  };

  // Fetch complete invoice details when an invoice is selected
  const handleSelectInvoice = async (invoice) => {
    try {
      setLoading(true);
      console.log('Fetching complete invoice details for:', invoice.invoiceNo);
      const data = await salesService.getSalesInvoiceByInvoiceNo(invoice.invoiceNo);
      console.log('Complete invoice data:', data);
      if (data.success) {
        // Pass the complete API response to the parent component
        // This ensures all fields are available for the debit note
        console.log('Passing complete API response to parent:', data);
        onSelectInvoice(data);
        onClose();
      } else {
        console.error('Failed to fetch complete invoice details:', data.message);
        // Show error message to user
        alert('Failed to fetch invoice details. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      alert('Error fetching invoice details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        p: 2
      }}>
        <Typography variant="h6" fontWeight={600}>
          {dialogTitle}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {/* Search and Filters */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 1.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <TextField
                placeholder="Search by invoice number or customer name"
                value={searchQuery}
                // Only update the search query state, don't trigger a search
                onChange={(e) => setSearchQuery(e.target.value)}
                // Prevent Enter key from submitting the form
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                fullWidth
                size="small"
                sx={{
                  ...CONTROL_STYLE,
                  mt: 1.5,
                  '& .MuiInputBase-root': {
                    height: '32px'
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                sx={{
                  mt: 1.5,
                  ml: 1,
                  height: '32px',
                  minWidth: 'auto',
                  borderRadius: 0,
                  px: 2
                }}
                startIcon={<SearchIcon fontSize="small" />}
              >
                Search
              </Button>
            </Box>
          </Box>

          {/* Filter options removed */}
        </Box>

        {/* Invoices Table */}
        <TableContainer component={Paper} sx={{ 
          boxShadow: 'none', 
          border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: '0px',
          mb: 2
        }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: '#f5f5f5',
                '& th': {
                  py: 1,
                  fontSize: '0.75rem',
                  fontWeight: 700
                }
              }}>
                <TableCell>Invoice #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : searchQuery.trim() && invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Search....
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : searchQuery.trim() ? (
                // Show search results
                invoices.map((invoice) => (
                  <TableRow 
                    key={invoice._id}
                    hover
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        cursor: 'pointer'
                      },
                      '& td': {
                        py: 0.75
                      }
                    }}
                    onClick={() => handleSelectInvoice(invoice)}
                  >
                    <TableCell>{invoice.invoiceNo}</TableCell>
                    <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {invoice.customerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {invoice.customerPhone || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatCurrency(invoice.totalAmt, currencyIcon)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={invoice.paymentStatus} 
                        size="small"
                        color={getStatusColor(invoice.paymentStatus)}
                        sx={{ 
                          height: 24,
                          textTransform: 'capitalize',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectInvoice(invoice);
                        }}
                        sx={{ 
                          borderRadius: 0,
                          py: 0.25,
                          px: 1,
                          minWidth: 'auto',
                          textTransform: 'none',
                          fontSize: '0.75rem'
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : defaultInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No invoices available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                // Show default invoices
                defaultInvoices.map((invoice) => (
                  <TableRow 
                    key={invoice._id}
                    hover
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        cursor: 'pointer'
                      },
                      '& td': {
                        py: 0.75
                      }
                    }}
                    onClick={() => handleSelectInvoice(invoice)}
                  >
                    <TableCell>{invoice.invoiceNo}</TableCell>
                    <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {invoice.customerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {invoice.customerPhone || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatCurrency(invoice.totalAmt, currencyIcon)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={invoice.paymentStatus} 
                        size="small"
                        color={getStatusColor(invoice.paymentStatus)}
                        sx={{ 
                          height: 24,
                          textTransform: 'capitalize',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectInvoice(invoice);
                        }}
                        sx={{ 
                          borderRadius: 0,
                          py: 0.25,
                          px: 1,
                          minWidth: 'auto',
                          textTransform: 'none',
                          fontSize: '0.75rem'
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontSize: '0.875rem',
            },
            '.MuiTablePagination-select': {
              ...CONTROL_STYLE
            },
            '.MuiToolbar-root': {
              minHeight: '40px',
              height: '40px',
              paddingTop: 0,
              paddingBottom: 0
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}